import { Check, SearchLg, ShieldPlus } from '@untitledui/icons'
import { useState } from 'react'

import { Input } from '@/lib/components/designSystem/input/input'
import Button from '@components/designSystem/Button'
import Dialog from '@components/designSystem/Dialog/Dialog'
import { Table } from '@components/designSystem/Table'
import { Badge, BadgeWithIcon } from '@components/designSystem/badges/badges'
import useDebounce from '@hooks/useDebounce.ts'
import useToast from '@hooks/useToast.ts'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { getRoleBadgeColor } from '@routes/Administration/utils'
import { type Store, useAllStores } from '@state/store'
import { type StoreRole, useAddStoreGrant } from '@state/user'
import { doesStoreMatchSearch } from '@utils/storeSearch'

interface AddStoreGrantDialogProps {
    userId: string
    userFullname?: string
    alreadyAssignedStoreIds: string[]
    onClose: () => void
}

const ASSIGNABLE_STORE_ROLES: StoreRole[] = ['STORE_MANAGER', 'STORE_EMPLOYEE', 'STORE_VIEWER']

function AddStoreGrantDialog({ userId, userFullname, alreadyAssignedStoreIds, onClose }: AddStoreGrantDialogProps) {
  const { t } = useTypedTranslation()
  const { showToast } = useToast()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)

  const { data: stores = [], isLoading: storesLoading } = useAllStores(true)
  const { mutate, isPending, error } = useAddStoreGrant()
  const [selectedStore, setSelectedStore] = useState<Store>()
  const [selectedRole, setSelectedRole] = useState<StoreRole>()

  function handleSubmit() {
    if (!selectedStore || !selectedRole) return
    mutate(
      { userId, storeId: selectedStore.id, role: selectedRole },
      {
        onSuccess: () => {
          showToast({
            title: t('admin.userManagement.dialog.addGrant.successTitle'),
            description: t('admin.userManagement.dialog.addGrant.successDescription', { userName: userFullname, storeName: selectedStore.name }),
            variant: 'success',
          })
          onClose()
        },
      })
  }

  const filteredStores = stores
    .filter(store => !(alreadyAssignedStoreIds.includes(store.id)))
    .filter(store => doesStoreMatchSearch(store, debouncedSearch))

  const isSaveDisabled = !selectedStore || !selectedRole || isPending

  return (
    <Dialog
      className='min-w-1/3'
      headerIcon={ShieldPlus}
      title={t('admin.userManagement.dialog.addGrant.title', { userName: userFullname })}
      size="md"
      onClose={isPending ? undefined : onClose}
      showCloseButton={!isPending}
      footer={
        <>
          <Button variant='outline' onClick={onClose} isDisabled={isPending}>{t('admin.userManagement.dialog.addGrant.cancel')}</Button>
          <Button variant='filled' onClick={handleSubmit} isDisabled={isSaveDisabled} isLoading={isPending}>{t('admin.userManagement.dialog.addGrant.assign')}</Button>
        </>
      }
    >
      <div className='flex flex-col gap-6'>
        <section className='flex flex-col gap-3'>
          <h2 className='text-sm font-medium text-secondary'>{t('admin.userManagement.dialog.addGrant.selectStoreHeading')}</h2>
          <Input leadingIcon={{ icon: SearchLg }} value={search} onChange={setSearch} placeholder={t('admin.userManagement.dialog.addGrant.storePlaceholder')} />

          <div className="max-h-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <Table
              aria-label="Store selection table"
              selectionMode="single"
              selectionBehavior="toggle"
              selectedKeys={selectedStore ? [selectedStore.id] : []}
              onSelectionChange={(keys) => {
                const selected = Array.from(keys)[0]
                const selectedStore = stores.find(store => store.id === selected)
                setSelectedStore(selectedStore)
              }}
            >
              <Table.Header>
                <Table.Head isRowHeader label={t('admin.userManagement.dialog.addGrant.storeNameColumn')} />
                <Table.Head label={t('admin.userManagement.dialog.addGrant.addressColumn')} />
              </Table.Header>
              <Table.Body
                className='overflow-y-auto'
                items={filteredStores}
                renderEmptyState={() => {
                  if (storesLoading) {
                    return (
                      <div className="py-8 text-center text-sm text-gray-500">
                        {t('admin.userManagement.dialog.addGrant.loadingStores')}
                      </div>
                    )
                  }

                  const hasAvailableStores = stores.some(store => !alreadyAssignedStoreIds.includes(store.id))

                  return (
                    <div className="py-8 text-center text-sm text-gray-500">
                      {!hasAvailableStores
                        ? t('admin.userManagement.dialog.addGrant.allStoresAssigned')
                        : t('admin.userManagement.dialog.addGrant.noStoresFoundWithSearch')}
                    </div>
                  )
                }}
              >
                {(item) => (
                  <Table.Row id={item.id}>
                    <Table.Cell className='text-primary'>{item.name}</Table.Cell>
                    <Table.Cell>{item.address ? `${item.address.addressLines[0]}, ${item.address.city}` : ''}</Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>
        </section>

        <section className='mt-3'>
          <h2 className='mb-2 text-sm font-medium text-secondary'>{t('admin.userManagement.dialog.addGrant.selectRoleHeading')}</h2>
          <div className='flex flex-wrap gap-4'>
            {ASSIGNABLE_STORE_ROLES.map(role => (
              <button key={role} className='cursor-pointer rounded-full' onClick={() => setSelectedRole(role)}>
                {role === selectedRole ? (
                  <BadgeWithIcon iconLeading={Check} color={getRoleBadgeColor(role)}>{t(`common.roles.store.${role}`)}</BadgeWithIcon>
                ) : (
                  <Badge color={getRoleBadgeColor(role)}>{t(`common.roles.store.${role}`)}</Badge>
                )}
              </button>
            ))}
          </div>
        </section>

        {error && <p className='text-sm text-red-600'>{error.message}</p>}
      </div>
    </Dialog>
  )
}

export default AddStoreGrantDialog